import React, { useState, useEffect, useRef } from 'react';
import { Building2, Loader2, MapPin, Shield, Clock, Edit, Save, Upload, X, Camera, AlertTriangle, MessageCircle, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API, API_BASE } from '../../config/apiBase';


export default function EmpresaConfigPage() {
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editGeo, setEditGeo] = useState(false);
    const [geoForm, setGeoForm] = useState({ latitude: '', longitude: '', radius: 250, enabled: true });
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoFailCount, setLogoFailCount] = useState(0);
    const [showDesignerPopup, setShowDesignerPopup] = useState(false);
    const logoInputRef = useRef(null);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/ponto/empresa`, { headers }).then(r => r.json())
            .then(data => {
                setEmpresa(data);
                if (data?.geofence) setGeoForm({ latitude: data.geofence.latitude || '', longitude: data.geofence.longitude || '', radius: data.geofence.radius || 250, enabled: data.geofence.enabled ?? true });
                setLoading(false);
            }).catch(() => setLoading(false));
    }, []);

    const saveGeofence = async () => {
        try {
            const r = await fetch(`${API}/ponto/empresa/geofence`, { method: 'PUT', headers, body: JSON.stringify(geoForm) });
            if (!r.ok) throw new Error('Erro');
            toast.success('Geofence atualizado!');
            setEditGeo(false);
            setEmpresa(prev => ({ ...prev, geofence: { ...geoForm } }));
        } catch { toast.error('Erro ao salvar'); }
    };

    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'image/png') {
            toast.error('Apenas PNG permitido');
            setLogoFailCount(c => c + 1);
            if (logoFailCount + 1 >= 3) setShowDesignerPopup(true);
            return;
        }
        if (file.size > 512 * 1024) {
            toast.error('Maximo 512KB');
            setLogoFailCount(c => c + 1);
            if (logoFailCount + 1 >= 3) setShowDesignerPopup(true);
            return;
        }
        const img = new window.Image();
        img.onload = () => {
            const ratio = img.width / img.height;
            if (ratio < 2.0 || ratio > 4.0) {
                toast.error(`Proporcao ${ratio.toFixed(1)}:1 invalida. Use entre 2:1 e 4:1`);
                setLogoFailCount(c => c + 1);
                if (logoFailCount + 1 >= 3) setShowDesignerPopup(true);
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        };
        img.src = URL.createObjectURL(file);
    };

    const uploadLogo = async () => {
        if (!logoFile) return;
        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', logoFile);
            const r = await fetch(`${API}/empresa/logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
            if (r.ok) {
                const data = await r.json();
                toast.success('Logo atualizada!');
                setEmpresa(prev => ({ ...prev, logo_url: data.logo_url }));
                setLogoFile(null);
                setLogoPreview(null);
                setLogoFailCount(0);
            } else {
                const err = await r.json();
                toast.error(err.detail || 'Erro');
                setLogoFailCount(c => c + 1);
                if (logoFailCount + 1 >= 3) setShowDesignerPopup(true);
            }
        } catch { toast.error('Erro de conexão'); }
        finally { setLogoUploading(false); }
    };

    const cancelLogo = () => {
        setLogoFile(null);
        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoPreview(null);
    };

    if (loading || !empresa) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="empresa-config">
            {/* Dados Principais */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-gold-primary)' }}>
                        {empresa.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{empresa.name}</h3>
                        <p className="text-xs" style={{ color: 'var(--muted)' }}>ID: {empresa.empresa_id}</p>
                    </div>
                </div>
                <div className="space-y-2">
                    {[
                        { label: 'CNPJ', value: empresa.cnpj || 'Não informado' },
                        { label: 'Status', value: empresa.status === 'active' ? 'Ativo' : empresa.status },
                        { label: 'Plano', value: empresa.plan?.charAt(0).toUpperCase() + empresa.plan?.slice(1) },
                        { label: 'Limite Colaboradores', value: empresa.max_employees || 10 },
                        { label: 'Criado em', value: empresa.created_at ? new Date(empresa.created_at).toLocaleDateString('pt-BR') : '-' },
                    ].map((item) => (
                        <div key={item.label} className="flex justify-between p-3 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>{item.label}</span>
                            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Geofence */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <MapPin size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Geofence</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: empresa.geofence?.enabled ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.12)', color: empresa.geofence?.enabled ? '#10B981' : '#6B7280' }}>
                            {empresa.geofence?.enabled ? 'Ativo' : 'Inativo'}
                        </span>
                        <button onClick={() => setEditGeo(!editGeo)} className="p-1.5 rounded-lg transition-colors" style={{ background: 'var(--surface-2)' }}>
                            <Edit size={12} style={{ color: 'var(--muted)' }} />
                        </button>
                    </div>
                </div>
                {editGeo ? (
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px]" style={{ color: 'var(--muted)' }}>Latitude</label>
                                <input type="number" step="any" value={geoForm.latitude} onChange={e => setGeoForm({...geoForm, latitude: parseFloat(e.target.value)})}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            </div>
                            <div>
                                <label className="text-[10px]" style={{ color: 'var(--muted)' }}>Longitude</label>
                                <input type="number" step="any" value={geoForm.longitude} onChange={e => setGeoForm({...geoForm, longitude: parseFloat(e.target.value)})}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px]" style={{ color: 'var(--muted)' }}>Raio (m)</label>
                                <input type="number" value={geoForm.radius} onChange={e => setGeoForm({...geoForm, radius: parseInt(e.target.value)})}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            </div>
                            <div>
                                <label className="text-[10px]" style={{ color: 'var(--muted)' }}>Status</label>
                                <select value={geoForm.enabled ? 'true' : 'false'} onChange={e => setGeoForm({...geoForm, enabled: e.target.value === 'true'})}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                    <option value="true">Ativo</option>
                                    <option value="false">Inativo</option>
                                </select>
                            </div>
                        </div>
                        <Button onClick={saveGeofence} className="btn-gold text-sm"><Save size={12} className="mr-1" /> Salvar</Button>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <div className="flex justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>Coordenadas</span>
                            <span className="text-xs" style={{ color: 'var(--text)' }}>{empresa.geofence?.latitude?.toFixed(4)}, {empresa.geofence?.longitude?.toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>Raio</span>
                            <span className="text-xs" style={{ color: 'var(--text)' }}>{empresa.geofence?.radius}m</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Parametros */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Parametros do Sistema</h3>
                <div className="space-y-1.5">
                    <div className="flex justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>Precisao GPS minima</span>
                        <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{empresa.config?.precision_limit || 100}m</span>
                    </div>
                    <div className="flex justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>Geo obrigatoria para todos</span>
                        <span className="text-xs font-medium" style={{ color: empresa.config?.require_geo_all ? '#10B981' : 'var(--text)' }}>
                            {empresa.config?.require_geo_all ? 'Sim' : 'Nao'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Identidade Visual - Logo */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="logo-upload-section">
                <div className="flex items-center gap-2 mb-3">
                    <Image size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Identidade Visual</h3>
                </div>
                <p className="text-[11px] mb-3" style={{ color: 'var(--muted)' }}>Envie a logo da sua empresa. Formato: PNG, proporcao 2:1 a 4:1, maximo 512KB.</p>

                {/* Current / Preview */}
                <div className="flex items-center gap-4 mb-3">
                    <div className="w-32 h-12 rounded-lg overflow-hidden flex items-center justify-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                        {logoPreview ? (
                            <img src={logoPreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                        ) : empresa.logo_url ? (
                            <img src={`${API_BASE}${empresa.logo_url}`} alt="Logo" className="max-w-full max-h-full object-contain" />
                        ) : (
                            <div className="text-[10px] text-center" style={{ color: 'var(--muted)' }}>Sem logo</div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        {logoPreview ? (
                            <div className="flex gap-2">
                                <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={uploadLogo} disabled={logoUploading} data-testid="logo-save-btn">
                                    {logoUploading ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Salvar
                                </button>
                                <button className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px]" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={cancelLogo}>
                                    <X size={11} /> Cancelar
                                </button>
                            </div>
                        ) : (
                            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted)' }} onClick={() => logoInputRef.current?.click()} data-testid="logo-upload-btn">
                                <Upload size={11} /> Enviar Logo
                            </button>
                        )}
                    </div>
                </div>
                <input ref={logoInputRef} type="file" accept="image/png" onChange={handleLogoSelect} className="hidden" data-testid="logo-file-input" />
            </div>

            {/* Designer Popup */}
            {showDesignerPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
                    <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)' }} data-testid="designer-popup">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle size={20} style={{ color: '#F59E0B' }} />
                            <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>Problemas com a logo?</h3>
                        </div>
                        <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
                            Esta tendo problema com a identidade visual (logo) da sua empresa? Recomendamos um freelancer designer. Clique no botao e contrate.
                        </p>
                        <div className="flex gap-2">
                            <a href="https://wa.me/5511999999999?text=Preciso%20de%20ajuda%20com%20a%20logo%20da%20minha%20empresa%20no%20SuperVitrine" target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold" style={{ background: '#25D366', color: '#fff' }} data-testid="contact-designer-btn">
                                <MessageCircle size={13} /> Contatar Designer
                            </a>
                            <button className="px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => setShowDesignerPopup(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
